import { HROptionTypes } from '@bimser/common';
import { BCInput } from '@bimser/components';
import { FormItemTypes, IDictionary, IItem, unquotedJson } from '@bimser/core';
import dayjs from 'dayjs';
import { JSX } from 'react';
import { UserMetadataType } from '../../../../entities';
import { IPropertyField, IPropertyFieldType, SizeType } from '../../../../entities/common';
import MetadataControl from '../../base/MetadataControl';
import { filterFields } from '../../helper';
import Styles from './assets/style.scss';
import { IUserMetadataProperties } from './entities/IUserMetadataProperties';
import localizedFormat from 'dayjs/plugin/localizedFormat';
dayjs.extend(localizedFormat);

export default class UserMetadata extends MetadataControl<UserMetadataType> {
	sizeType: SizeType;
	customMetadataName: string;
	__customMetadataList: IItem[] = [];

	constructor() {
		super();
		this.controlType = FormItemTypes.EControlTypes.UserMetadata;
	}

	__loadProperties(data: IUserMetadataProperties) {
		super.__loadProperties(data);

		this.__proxyChanged.text = this.__getText(data.value);
		this.__proxyChanged.customMetadataName = data.customMetadataName;
		this.__proxyChanged.sizeType = data.sizeType;
	}

	__setCustomMetadataList(list: IItem[]) {
		this.__customMetadataList = list;
		this.__proxyChanging.text = this.__getText(this.value);
	}

	__getText(value: string) {
		const types = [
			'NameAndSurname',
			'UserId',
			'UserName',
			'Position',
			'PositionId',
			'Profession',
			'ProfessionId',
			'Department',
			'DepartmentId',
			'EmailAddress',
			'Location',
			'Company',
			'CompanyId',
			'CompanyName'
		];
		let propertyType: HROptionTypes = HROptionTypes.Text;

		if (this.__customMetadataList?.length) {
			const item = this.__customMetadataList.find(
				_item => _item.key === this.customMetadataName
			);
			propertyType = item?.externalData?.propertyType || HROptionTypes.Text;
		}

		if (!types.includes(this.metadataType) && HROptionTypes.Date === propertyType) {
			const date = dayjs(this.value);

			if (date.isValid()) {
				return date.format('L');
			}
		}

		if (this.metadataType === 'Location') {
			const loc = JSON.parse(unquotedJson(value) || '{}');
			return Object.keys(loc).length ? `${loc.lat}, ${loc.lon}` : '';
		}

		return this.text || value;
	}

	__getValuePropertyFieldType(properties: Map<string, any>): IPropertyFieldType {
		const stringTypes = [
			'NameAndSurname',
			'UserName',
			'Position',
			'Department',
			'Profession',
			'EmailAddress',
			'Location',
			'Company',
			'CompanyName'
		];
		const integerTypes = ['UserId', 'PositionId', 'DepartmentId', 'ProfessionId', 'CompanyId'];

		if (stringTypes.includes(properties.get('metadataType'))) {
			return 'string';
		} else if (integerTypes.includes(properties.get('metadataType'))) {
			return 'number';
		} else if (properties.get('metadataType') === 'Custom') {
			return 'any';
		}
		return 'string';
	}

	__getPropertyFields(properties?: Map<string, any>): IPropertyField[] {
		const fields = super.__getPropertyFields();
		return [
			...filterFields(fields, ['value']),
			{
				name: 'value',
				type: this.__getValuePropertyFieldType(properties)
			}
		];
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			sizeType: this.sizeType,
			customMetadataName: this.customMetadataName,
			text: this.text
		};
	}

	__getSizeType() {
		return this.sizeType ?? this.__form.sizeType;
	}

	__render(children?: JSX.Element) {
		return super.__render(
			<BCInput
				value={this.loading ? '' : this.__getText(this.value)}
				style={this.style}
				readOnly
				disabled={this.__isDisabled()}
				className={Styles.transparentContent}
				size={this.__getSizeType()}
			/>
		);
	}
}
